import { LoopVideo } from '@/components/landing/loop-video';
import { INSIDE, SCREENS, SCREEN_SIZE } from '@/content/premize-3d';

/**
 * Everything the PreMize 3D shows, as plain HTML: the product photo, the six
 * layers, and the standout features as muted loops with captions.
 *
 * It is the fallback when the 3D does not run (and what the prerender bakes),
 * and the text crawlers and screen readers get either way. When the 3D is on,
 * the parts the 3D already shows are hidden visually but stay in the DOM:
 * the photo always; the layer list from 1024px, where the 3D labels its
 * layers; the feature tiles where the 3D also has the height to play them on
 * its screens. Otherwise they stay visible under the 3D.
 */
export function PremizeInside() {
  return (
    <>
      <div className="p3d-intro grid items-center gap-10 lg:grid-cols-[1fr_1fr]">
        <div className="p3d-photo product-shot mx-auto w-full max-w-[560px]">
          <img
            loading="lazy"
            decoding="async"
            src="/img/spark-hero.webp"
            width={716}
            height={257}
            alt="NVIDIA DGX Spark, the computer PreMize runs on"
            className="w-full rounded-lg"
          />
        </div>
        <ol className="p3d-layers" aria-label="Inside PreMize, from the hardware up">
          {INSIDE.map((l) => (
            <li key={l.n} className="flex gap-4 border-t border-border py-3.5">
              <span className="w-7 shrink-0 font-medium tabular-nums text-primary">{l.n}</span>
              <span>
                <span className="block font-semibold text-foreground">{l.name}</span>
                <span className="block text-[15px] text-muted-foreground">{l.line}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>

      {SCREENS.length > 0 && (
        <div className="p3d-features mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SCREENS.map((s) => (
            <figure key={s.id}>
              <LoopVideo
                src={s.src}
                poster={s.poster}
                width={SCREEN_SIZE.width}
                height={SCREEN_SIZE.height}
                label={`${s.title}: ${s.line}`}
                className="aspect-[16/10] w-full rounded-xl bg-card object-cover"
              />
              <figcaption className="mt-4">
                <span className="block text-[15px] font-semibold text-foreground">{s.title}</span>
                <span className="mt-1 block text-[15px] leading-snug text-muted-foreground">{s.line}</span>
                {s.meta && <span className="t-caption mt-2 block">{s.meta}</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </>
  );
}
